import React, { useEffect, useCallback } from 'react';
import { EVENT_CHECK_AUTH } from 'App/components/WebSocket';
import {
  registerRequestInterceptor,
  registerErrorInterceptor,
  defaultBaseURL,
} from 'api';

import { useSubscribe } from '@gogame/hooks-ts';
import { useScreenMessage } from '@gogame/screens';
import { useSocket } from '@gogame/sockets';

import AuthContext from './AuthContext';
import { useUserManager, useImpersonate } from './User';
import { useSocketHandShake } from 'App/components/WebSocket';

export const SecurityProvider = ({ children, auth, isPartner }) => {
  const { userState, getUser, blockUser, cleanUser, loading } =
    useUserManager();
  const { showErrorMessage } = useScreenMessage();
  const socket = useSocket();
  const {
    impersonateToken,
    getImpersonateToken,
    impersonate,
    dismissImpersonate,
  } = useImpersonate();
  const socketHandShake = useSocketHandShake();

  const handleLogout = () => {
    auth.logout();
  };

  const handleHandshake = useCallback(() => {
    if (userState.user?.handshakeToken) {
      socketHandShake(userState?.user?.handshakeToken);
    }
  }, [userState?.user?.handshakeToken]);

  useEffect(() => {
    (async () => {
      if (auth.isAuthenticated && socket?.id) {
        handleHandshake();
      }
    })();
  }, [handleHandshake, auth.isAuthenticated, socket?.id]);

  useSubscribe(
    () =>
      registerRequestInterceptor(async (request) => {
        const accessToken = await auth.getAccessToken();
        if (accessToken && request?.baseURL === defaultBaseURL) {
          request.headers.set('Authorization', `Bearer ${accessToken}`);
        }

        return request;
      }, 'AuthToken Request'),
    [auth.getAccessToken]
  );

  useSubscribe(
    () =>
      registerRequestInterceptor(async (request) => {
        if (
          userState.user?.handshakeToken &&
          request?.baseURL === defaultBaseURL
        ) {
          request.headers.set('X-User-Data', userState.user?.handshakeToken);
        }

        return request;
      }, 'X-User-Data'),
    [userState.user?.handshakeToken]
  );

  useSubscribe(
    (isAuthenticated) => {
      if (isAuthenticated) {
        return registerErrorInterceptor(async (error) => {
          // eslint-disable-next-line no-console
          if (error?.status === 401) {
            showErrorMessage(`Access denied`);
            auth.onUnauthorized();
          } else if (error?.status === 403) {
            const response = error.response;
            const body = response?.debug?.body;
            if (
              body &&
              (body.errorType === 'blocked' || body.errorType === 7)
            ) {
              const user = body?.errors?.[0]?.data;
              blockUser(user);
            } else {
              throw error;
            }
          } else {
            throw error;
          }
        }, 'Access denied');
      }
    },
    [auth.isAuthenticated, auth._name]
  );

  const handleImpersonate = async ({ userId }) => {
    await impersonate({ userId });
  };

  const handleDismissImpersonate = async () => {
    await dismissImpersonate();
  };

  useEffect(() => {
    if (auth?.isLoginRedirect?.()) {
      auth.callback();
    }
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) {
      // eslint-disable-next-line no-console
      // console.debug('[SecurityProvider] useEffect load user');
      getUser();
    } else if (userState.user) {
      // eslint-disable-next-line no-console
      // console.debug('[SecurityProvider] Clear user state');
      cleanUser();
    }
    //reload user after impersonate
  }, [auth.isAuthenticated, impersonateToken]);

  useSubscribe(() => {
    const handleGetUser = () => {
      // eslint-disable-next-line no-console
      console.debug('[Socket] Check auth');
      return getUser();
    };
    socket.on(EVENT_CHECK_AUTH, handleGetUser);
    return () => socket.off(EVENT_CHECK_AUTH, handleGetUser);
  }, [socket]);

  //first connect will ignore because provider already connected
  //second and other connect emit getUser
  useSubscribe(() => {
    const handleGetUser = () => {
      if (auth.isAuthenticated && userState.user) {
        // eslint-disable-next-line no-console
        console.debug('[Socket] Connect -> get User');
        return getUser();
      } else {
        // eslint-disable-next-line no-console
        console.debug('[Socket] Connect -> unauthorized User');
      }
    };
    socket.on('connect', handleGetUser);
    return () => socket.off('connect', handleGetUser);
  }, [socket, userState.user, auth.isAuthenticated]);

  const pending = !!(auth.active && auth.loaded && !auth.isAuthenticated);

  return (
    <AuthContext.Provider
      value={{
        isPartner,
        impersonated: userState?.impersonated ?? false,
        getAccessToken: auth.getAccessToken,
        getImpersonateToken: getImpersonateToken,
        userState: userState,
        loading: loading,
        logout: handleLogout,
        login: auth.login,
        impersonate: handleImpersonate,
        dismissImpersonate: handleDismissImpersonate,
        ready:
          auth.loaded &&
          !pending &&
          (auth.isAuthenticated ? userState.user : true),
        // disable google button for incognito
        // https://github.com/anthonyjgrove/react-google-login/issues/378
        hasError: auth.hasError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default SecurityProvider;
